/**
 * SED Amendment Operations — extract, apply, and manage amendments.
 */

import type { SiteEvidenceDocument, EvidenceSection, PublicSentimentSection } from '../types/siteEvidence';
import type { SiteContext, ExtractedClaim } from '../types/siteContext';
import type { SEDVariable, SEDAmendment } from '../types/sedAmendments';
import type { ProjectIntent } from '../types/siteRag';
import { authedFetch } from '../lib/authedFetch';
import { indexAuthoredStructuredFields } from '../lib/coverage/authoredSedOverlay';
// SA6 reverse-sync (Adopt-Context) — the binding registry is the static,
// code-reviewed allow-list of paths a context claim may legitimately write
// into the SED. `applySingleAmendmentToSed` consults it to decide whether a
// missing intermediate is a KNOWN-but-unpopulated bound section (safe to
// vivify) versus genuine schema drift / a typo (must still throw, UD3).
import { getBindingForSedPath } from './siteContext/sedBindingRegistry';
// #sed-owner-path — a SED's object key is OWNER-scoped and is built in ONE
// place. Hardcoding `users/{callerUid}/…` here forked shared sites (module
// header: Redhead 2026-07-27, pointer v18 vs canonical file v14).
import { resolveSedInPlaceSavePath } from '../lib/sed/sedStoragePath';
import {
  applySingleAmendmentToSedPure,
  deriveAuthoritativeSedAmendments,
  getSedAmendmentStatusPrecedence,
  readValueAtSEDPathPure,
  resolveRevertLineageOnSed,
  validateAmendmentPathOnSedPure,
  type SEDAmendmentLifecycleEvent,
} from '../lib/sed/sedAmendmentMutationCore';

export {
  applySingleAmendmentToSedPure,
  deriveAuthoritativeSedAmendments,
  getSedAmendmentStatusPrecedence,
  readValueAtSEDPathPure,
  resolveRevertLineageOnSed,
  validateAmendmentPathOnSedPure,
  type SEDAmendmentLifecycleEvent,
};
// GAP 3 (field-architecture wiring) — the SED-variable browser must surface the
// NEW 3-axis BTM model (cooling axes + power-basis + ELCC / firm-capacity), not
// only the 17 legacy provider sections. Resolve them through the SAME canonical
// helpers the prompts + Go/No-Go consume so the browser never drifts.
import { projectCoolingGeneration, resolveCoolingPair } from '../types/siteRag';
import { resolveIntentPowerStack } from '../lib/powerStack';
import { summarizeIntentFeasibility } from '../lib/phaseFeasibility';
import { documentFieldEntries, currentDocumentFieldSource, unitForDocumentField } from '../lib/sed/documentFieldView';

// ── Domain metadata for the variable browser ──

const DOMAIN_LABELS: Record<string, string> = {
  climate: 'Climate',
  waterStress: 'Water Stress',
  waterResource: 'Water and Wastewater Service',
  geotechnical: 'Geotechnical Evidence',
  survey: 'Professional Survey',
  mineralResources: 'Mineral Resources and Title',
  culturalResources: 'Cultural Resources',
  seismic: 'Seismic',
  grid: 'Grid Infrastructure',
  flood: 'Flood Risk',
  environmental: 'Environmental',
  nuclearInfra: 'Nuclear Infrastructure',
  solar: 'Solar Resource',
  wind: 'Wind Resource',
  electricity: 'Electricity Pricing',
  airQuality: 'Air Quality',
  connectivity: 'Connectivity',
  parcel: 'Parcel Data',
  regulatory: 'Regulatory',
  siteResearch: 'Site Research',
  siteProfile: 'Site Profile',
  onSiteInfrastructure: 'On-site Infrastructure',
  // v1.2.1 HR-5 — BTM grounding sections.
  cooling: 'Cooling (BTM Model)',
  power: 'Power Semantics',
  firmCapacity: 'Firm Capacity (ELCC)',
  lcoe: 'LCOE',
  fuelSupply: 'Fuel Supply (Natural Gas)',
  technology: 'Technology',
};

const VARIABLE_UNITS: Record<string, string> = {
  avgTemp: '°C', peakTemp: '°C', minTemp: '°C', avgWetBulb: '°C', peakWetBulb: '°C',
  humidity: '%', annualPrecipitation: 'mm',
  pga: 'g', sds: 'g', sd1: 'g',
  substationDistKm: 'km',
  bwsScore: '/5', bwsRaw: 'ratio', overallWaterRisk: '/5',
  ghi: 'kWh/m²/day', dni: 'kWh/m²/day',
  windSpeed100m: 'm/s', windSpeed80m: 'm/s', capacityFactor: '',
  retailRate: '¢/kWh',
  aqi: '', pm25Conc: 'µg/m³',
  nearestIxDistKm: 'km', nearestReactorDistKm: 'km',
  nearestTransmissionKm: 'km',
  nearestPipelineKm: 'km',
  nearestPipelineMassFlowValue: '',
  nearestPipelineAvailabilityPct: '%',
  acres: 'acres', perimeterKm: 'km', elevationMin: 'm', elevationMax: 'm', elevationAvg: 'm', slopePercent: '%',
  // v1.2.1 HR-5 — BTM grounding units.
  resolvedPue: '', pueValue: '', powerFactor: '',
  declaredMw: 'MW', itLoadMw: 'MW', facilityMw: 'MW', apparentMva: 'MVA', targetMw: 'MW',
  naturalGasPriceUsdPerMmbtu: '$/MMBtu', henryHubSpotUsdPerMmbtu: '$/MMBtu',
  fuelCostPerMwhUsd: '$/MWh', heatRateBtuPerKwh: 'Btu/kWh',
  blendedLcoePerMwhUsd: '$/MWh', discountRate: '', lifetimeYears: 'yr', capacityFactorPct: '%',
  typicalModuleMWe: 'MWe',
  avgMgd: 'MGD', peakMgd: 'MGD', permittedMgd: 'MGD', excessMgd: 'MGD', storageGallons: 'US gal', nearestMainMiles: 'mi',
  consumptiveUseAfy: 'acre-ft/yr', aquiferDepthToWaterFt: 'ft', surveyedAcreage: 'acres', contourIntervalFt: 'ft',
  bearingCapacityKPa: 'kPa', presumptiveBearingPressureKPa: 'kPa', boreholeDepthM: 'm', groundwaterDepthM: 'm',
  depthToBedrockM: 'm', seasonalWaterTableDepthM: 'm', acreageSurveyed: 'acres',
};

const VARIABLE_LABELS: Record<string, string> = {
  avgTemp: 'Average Temperature', peakTemp: 'Peak Temperature', minTemp: 'Minimum Temperature',
  avgWetBulb: 'Average Wet Bulb', peakWetBulb: 'Highest Monthly Wet Bulb (Historical Screening)',
  humidity: 'Relative Humidity', annualPrecipitation: 'Annual Precipitation',
  pga: 'Peak Ground Acceleration', sds: 'SDS (Short-Period)', sd1: 'SD1 (1-Second)',
  designCategory: 'Seismic Design Category', riskCategory: 'Risk Category', siteClass: 'Site Class',
  substationDistKm: 'Distance to Substation', substationName: 'Substation Name',
  voltage: 'Transmission Voltage', capacity: 'Grid Capacity', complexity: 'Interconnection Complexity',
  nearestTransmissionKm: 'Distance to Transmission Corridor',
  bwsScore: 'Water Stress Score', bwsCategory: 'Water Stress Category', bwsLabel: 'Water Stress Label',
  bwsRaw: 'Withdrawal/Supply Ratio', overallWaterRisk: 'Overall Water Risk', stressLevel: 'Stress Level',
  floodZone: 'FEMA Flood Zone', sfha: 'Special Flood Hazard Area', floodRisk: 'Flood Risk Level',
  hasCriticalHabitat: 'Critical Habitat Present', species: 'Protected Species',
  hasWetlands: 'Wetlands Present', wetlandType: 'Wetland Type', wetlandAcres: 'Wetland Acreage',
  ghi: 'Global Horizontal Irradiance', dni: 'Direct Normal Irradiance',
  windSpeed100m: 'Wind Speed (100m)', windSpeed80m: 'Wind Speed (80m)', capacityFactor: 'Capacity Factor',
  retailRate: 'Industrial Electricity Rate', state: 'State', year: 'Data Year',
  aqi: 'Air Quality Index', category: 'AQI Category', primaryPollutant: 'Primary Pollutant', pm25Conc: 'PM2.5 Concentration',
  nearestIxName: 'Nearest IX', nearestIxDistKm: 'Distance to IX', facilityCount50km: 'DC Facilities (50km)',
  nearestReactorName: 'Nearest Reactor', nearestReactorDistKm: 'Distance to Reactor',
  reactorsWithin200km: 'Reactors Within 200km', nuclearInfraMaturity: 'Nuclear Infra Maturity',
  nuclearStance: 'Nuclear Permitting Stance', zoning: 'Zoning', stateNotes: 'State Notes',
  // DAT-FOLLOWUP-4 — present only when regulatory-db estimated the state.
  stateResolution: 'State Resolution (bounding-box estimate)',
  acres: 'Site Area', perimeterKm: 'Perimeter', elevationMin: 'Elevation (Min)',
  elevationMax: 'Elevation (Max)', elevationAvg: 'Elevation (Avg)', slopePercent: 'Terrain Slope',
  nearestPipelineKm: 'Distance to Pipeline',
  nearestPipelineOperator: 'Pipeline Operator',
  nearestPipelineMassFlowValue: 'Pipeline Mass Flow',
  nearestPipelineMassFlowUnit: 'Pipeline Mass Flow Unit',
  nearestPipelineMassFlowBasis: 'Pipeline Mass Flow Basis',
  nearestPipelineAvailabilityPct: 'Pipeline Availability',
  nearestPipelineGasCompositionSummary: 'Pipeline Gas Composition',
  // v1.2.1 HR-5 — BTM grounding labels.
  coolingStrategy: 'Cooling Strategy (Rack/IT)', bopHeatRejection: 'Heat-Rejection Sink',
  coolingGeneration: 'Cooling Generation Machine', resolvedPue: 'Resolved PUE', pueBasis: 'PUE Basis',
  powerBasis: 'Power Basis', declaredMw: 'Declared Power', pueValue: 'PUE', powerFactor: 'Power Factor',
  itLoadMw: 'IT Load', facilityMw: 'Facility Load', apparentMva: 'Apparent Power',
  basisProvenance: 'Power Basis Provenance',
  accreditationTarget: 'Firm-Capacity Target', targetMw: 'Firm-Capacity Target (MW)',
  accreditationNote: 'Accreditation Note', isoRegion: 'ISO Region',
  naturalGasPriceUsdPerMmbtu: 'Natural-Gas Price', priceSeries: 'Price Series',
  henryHubSpotUsdPerMmbtu: 'Henry Hub Spot', period: 'Price Period',
  fuelCostPerMwhUsd: 'Fuel Cost', heatRateBtuPerKwh: 'Heat Rate (assumed)', fuelCostBasis: 'Fuel Cost Basis',
  blendedLcoePerMwhUsd: 'Blended LCOE', discountRate: 'Discount Rate', lifetimeYears: 'Project Lifetime',
  capacityFactorPct: 'Capacity Factor', lcoeBasis: 'LCOE Basis',
  reactorDesignClass: 'Reactor Design Class', reactorDesignLabel: 'Reactor Design Label', fuelClass: 'Fuel Class',
  licensingPathwaySummary: 'Licensing Pathway', nuclearInterest: 'Nuclear Interest', typicalModuleMWe: 'Typical Module MWe',
  typicalCodYears: 'Typical COD Years', reactorVendorId: 'Reactor Vendor ID', reactorVendorLabel: 'Reactor Vendor Label', nrcDocketId: 'NRC Docket ID',
};

/** Internal siteProfile keys — sampled for elevation math but not useful in the variable browser. */
const EXCLUDED_KEYS = new Set(['elevationStdev', 'elevationPointsSampled']);

/** GAP 3 — the domain label for the synthetic Project-Intent (BTM model) rows. */
const PROJECT_INTENT_DOMAIN = 'projectIntent';
const PROJECT_INTENT_DOMAIN_LABEL = 'Project Intent (BTM Model)';

/** Read-only leaves of the native flat sentiment section; no re-scoring or source promotion. */
function extractPublicSentimentVariables(section: PublicSentimentSection | undefined): SEDVariable[] {
  if (!section) return [];
  const variables: SEDVariable[] = [];
  // Partial source coverage stays anomalous; it is never labelled fully verified.
  const status: SEDVariable['status'] = section.status === 'ok' ? 'verified'
    : section.status === 'partial' ? 'anomalous' : 'unavailable';
  const push = (path: string, label: string, value: unknown, confidence: number,
    source: string) => {
    if (value !== null && typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') return;
    if (typeof value === 'number' && !Number.isFinite(value)) return;
    variables.push({ path: 'publicSentiment.' + path, label, domain: 'publicSentiment',
      domainLabel: 'Public Sentiment — ' + section.status, value, extractedValue: value,
      source, confidence, status, editable: false, isUserAmended: false,
      amendmentHistory: [], emptyReason: section.reason });
  };
  const source = 'Public Sentiment Engine — ' + section.status;
  push('status', 'Collection status', section.status, 0, source);
  if (section.reason !== undefined) push('reason', 'Collection limitations', section.reason, 0, source);
  for (const [index, bucket] of (section.perTech ?? []).entries()) {
    for (const key of ['btmTech', 'count', 'meanSentiment', 'meanConfidence'] as const) {
      push(`perTech.${index}.${key}`, `${bucket.btmTech} · ${key}`, bucket[key], bucket.meanConfidence, source);
    }
  }
  for (const [index, entry] of (section.entries ?? []).entries()) {
    for (const key of ['sourceId', 'fetchedAt', 'btmTech', 'sentimentScore', 'confidence', 'supportingSnippet',
      'url', 'scope', 'observedDate', 'dateBasis', 'scopeBasis', 'assessmentStatus', 'assessmentReason'] as const) {
      if (entry[key] !== undefined) push(`entries.${index}.${key}`, `Observation ${index + 1} · ${key}`,
        entry[key], entry.confidence, entry.sourceId);
    }
  }
  for (const [index, attempted] of (section.sourcesAttempted ?? []).entries()) {
    push(`sourcesAttempted.${index}`, `Attempted source ${index + 1}`, attempted, 0, source);
  }
  return variables;
}

/**
 * GAP 3 (field-architecture wiring) — surface the NEW 3-axis BTM model as
 * browsable, read-only SED variables so the variable browser reflects the model
 * the Go/No-Go + reports actually consume — NOT just the 17 legacy provider
 * sections. The fields read here:
 *
 *   - the THREE orthogonal cooling axes: rack-side `coolingStrategy`, the
 *     cold-END heat-rejection SINK `bopHeatRejection` (the water-withdrawal
 *     axis), and the BTM cooling-GENERATION machine `coolingGeneration`
 *     (resolved through the same `resolveCoolingPair` / `projectCoolingGeneration`
 *     boundary the prompts use, so the browser never prints a contradictory pair);
 *   - the POWER BASIS layers (entered MW + basis → IT load → facility/grid MW →
 *     apparent MVA), via `resolveIntentPowerStack`;
 *   - the FIRM-CAPACITY (ELCC) intent + the worst-case feasibility verdict, via
 *     `capacityCreditIntent` + `summarizeIntentFeasibility`.
 *
 * Prefers the full canonical `sed.fullProjectIntent` (the same shape that drives
 * Go/No-Go); falls back to the sparse `sed.projectIntent` view. Returns [] when
 * neither is present (legacy SEDs pass through unchanged). These rows are
 * read-only (`editable: false`) — they MIRROR the user's declared intent, which
 * is amended on the Project Intent form, not the SED browser.
 */
export function extractProjectIntentVariables(sed: SiteEvidenceDocument): SEDVariable[] {
  const full = sed.fullProjectIntent;
  const sparse = sed.projectIntent;
  if (!full && !sparse) return [];

  const out: SEDVariable[] = [];
  const push = (
    key: string,
    label: string,
    value: string | number | boolean | null,
    unit?: string,
  ): void => {
    if (value == null || value === '' || value === 'undecided' || value === 'unknown' || value === 'none') return;
    out.push({
      path: `${PROJECT_INTENT_DOMAIN}.${key}`,
      label,
      domain: PROJECT_INTENT_DOMAIN,
      domainLabel: PROJECT_INTENT_DOMAIN_LABEL,
      value,
      extractedValue: value,
      isUserAmended: false,
      unit,
      source: 'Project Intent (user-declared)',
      confidence: 1,
      status: 'verified-from-intent',
      // Read-only mirror — amended on the Project Intent form, not here.
      editable: false,
      amendmentHistory: [],
    });
  };

  // ── Cooling axes (three ORTHOGONAL axes — keep distinct) ──
  // Rack-side IT loop is a string on both full + sparse views.
  const coolingStrategy = full?.coolingStrategy ?? sparse?.coolingStrategy;
  push('coolingStrategy', 'Cooling Strategy (rack / IT-side)', coolingStrategy ?? null);

  // The cold-END SINK ↔ BTM MACHINE pair, normalized through the same boundary
  // the prompts use. Prefer the full intent (carries both raw axes); fall back
  // to the sparse view (carries the projected machine + the sink we now mirror).
  if (full) {
    const { coolingGeneration: gen, bopHeatRejection: sink } = resolveCoolingPair({
      coolingGeneration: full.coolingGeneration,
      bopHeatRejection: full.bopHeatRejection,
    });
    push('bopHeatRejection', 'Heat Rejection — Cold-End Sink (water-withdrawal axis)', sink ?? null);
    push('coolingGeneration', 'BTM Cooling Generation — Machine (PUE / waste-heat axis)', (gen ?? projectCoolingGeneration(full)) ?? null);
  } else if (sparse) {
    push('bopHeatRejection', 'Heat Rejection — Cold-End Sink (water-withdrawal axis)', sparse.bopHeatRejection ?? null);
    push('coolingGeneration', 'BTM Cooling Generation — Machine (PUE / waste-heat axis)', sparse.coolingGeneration ?? null);
  }

  // ── Power basis (IT → facility → grid → MVA) — only when a full intent ──
  // resolves a stack (the sparse view lacks PUE / basis to derive it).
  if (full) {
    const stack = resolveIntentPowerStack(full);
    if (stack.enteredMW != null) {
      push('powerBasis.basis', 'Power Basis (entered figure interpretation)', stack.basis);
      push('powerBasis.enteredMW', 'Entered Capacity', round1OrNull(stack.enteredMW), 'MW');
      push('powerBasis.itLoadMW', 'IT Load (critical)', round1OrNull(stack.itLoadMW), 'MW');
      push('powerBasis.facilityMW', 'Facility / Grid Demand', round1OrNull(stack.facilityMW), 'MW');
      push('powerBasis.apparentMVA', 'Apparent Power', round1OrNull(stack.apparentMVA), 'MVA');
      push('powerBasis.pue', 'PUE (facility ÷ IT)', round2OrNull(stack.pue));
    }
  }

  // ── Firm-capacity (ELCC) intent + worst-case feasibility verdict ──
  if (full) {
    const cc = full.capacityCreditIntent;
    if (cc?.target && cc.target !== 'none') {
      push('capacityCredit.target', 'Firm-Capacity (ELCC) Accreditation Target', cc.target);
      push('capacityCredit.targetMW', 'Firm-Capacity Target', round1OrNull(cc.targetMW ?? null), 'MW');
    }
    const feas = summarizeIntentFeasibility(full);
    if (feas.worstVerdict) {
      push('feasibility.worstVerdict', 'Firm-Capacity Feasibility (worst assessed phase)', feas.worstVerdict);
    }
  }

  return out;
}

function round1OrNull(v: number | null): number | null {
  return v == null || !Number.isFinite(v) ? null : Math.round(v * 10) / 10;
}
function round2OrNull(v: number | null | undefined): number | null {
  return v == null || !Number.isFinite(v) ? null : Math.round(v * 100) / 100;
}

/**
 * Extract all editable variables from a SED into a flat list for the variable browser.
 *
 * Provenance Per Variable (PRV-1, PRV-2) — when the optional `siteContext`
 * arg is supplied, every variable whose `path` matches an authored
 * claim's `sedField` (or `bindsToSedPaths`) also surfaces those
 * claims under `authoredClaims`. The browser's lineage strip reads
 * `authoredClaims[0]` to render the source-span + author chip for
 * `verified-from-context` rows.
 *
 * GAP 3 (field-architecture wiring) — also appends the NEW 3-axis BTM model
 * (cooling axes + power-basis + ELCC / firm-capacity) via
 * `extractProjectIntentVariables`, so the browser reflects the model the
 * Go/No-Go + reports consume, not only the legacy provider sections.
 */
export function extractSEDVariables(
  sed: SiteEvidenceDocument,
  pendingAmendments?: SEDAmendment[],
  siteContext?: SiteContext | null,
): SEDVariable[] {
  const variables: SEDVariable[] = [];
  const pendingByPath = new Map<string, SEDAmendment>();
  // LB3-2 — group every amendment (any status) by variablePath so each
  // variable carries its full audit trail. Sort newest-first so the
  // inline drawer's "most recent edit" copy reads naturally.
  const historyByPath = new Map<string, SEDAmendment[]>();
  for (const a of pendingAmendments ?? []) {
    if (a.status === 'pending') pendingByPath.set(a.variablePath, a);
    if (!historyByPath.has(a.variablePath)) historyByPath.set(a.variablePath, []);
    historyByPath.get(a.variablePath)!.push(a);
  }
  for (const list of historyByPath.values()) {
    list.sort((x, y) => (Date.parse(y.createdAt) || 0) - (Date.parse(x.createdAt) || 0));
  }

  // PRV-2 — index Site Context claims by every SED path they bind
  // to (the primary `sedField` plus every `bindsToSedPaths` entry).
  // Each entry holds an array because a single requirement can carry
  // multiple authored claims targeting the same field (e.g. updated
  // values authored in separate sessions). Newest-first is the same
  // ordering the lineage drawer uses.
  const claimsByPath = new Map<string, ExtractedClaim[]>();
  if (siteContext && Array.isArray(siteContext.claims)) {
    for (const claim of siteContext.claims) {
      const paths = new Set<string>();
      if (claim.sedField) paths.add(claim.sedField);
      for (const p of claim.bindsToSedPaths ?? []) paths.add(p);
      for (const p of paths) {
        if (!claimsByPath.has(p)) claimsByPath.set(p, []);
        claimsByPath.get(p)!.push(claim);
      }
    }
    for (const list of claimsByPath.values()) {
      list.sort((a, b) => {
        const ta = Date.parse(a.lastWrittenAt ?? '') || 0;
        const tb = Date.parse(b.lastWrittenAt ?? '') || 0;
        return tb - ta;
      });
    }
  }

  const authoredFieldIndex = siteContext
    ? indexAuthoredStructuredFields(siteContext.claims)
    : new Map<string, { value: unknown; claimId: string }>();

  const evidenceSections: [string, EvidenceSection<any> | undefined][] = [
    ['climate', sed.climate],
    ['waterStress', sed.waterStress],
    ['waterResource', sed.waterResource],
    ['geotechnical', sed.geotechnical],
    ['survey', sed.survey],
    ['mineralResources', sed.mineralResources],
    ['culturalResources', sed.culturalResources],
    ['seismic', sed.seismic],
    ['grid', sed.grid],
    ['flood', sed.flood],
    ['environmental', sed.environmental],
    ['nuclearInfra', sed.nuclearInfra],
    ['solar', sed.solar],
    ['wind', sed.wind],
    ['electricity', sed.electricity],
    ['airQuality', sed.airQuality],
    ['connectivity', sed.connectivity],
    ['parcel', sed.parcel],
    ['regulatory', sed.regulatory],
    ['siteResearch', sed.siteResearch],
    ['siteProfile', sed.siteProfile],
    ['onSiteInfrastructure', sed.onSiteInfrastructure as EvidenceSection<any> | undefined],
    // v1.2.1 HR-5 — BTM grounding sections.
    ['cooling', sed.cooling as EvidenceSection<any> | undefined],
    ['power', sed.power as EvidenceSection<any> | undefined],
    ['firmCapacity', sed.firmCapacity as EvidenceSection<any> | undefined],
    ['lcoe', sed.lcoe as EvidenceSection<any> | undefined],
    ['fuelSupply', sed.fuelSupply as EvidenceSection<any> | undefined],
    ['technology', sed.technology as EvidenceSection<any> | undefined],
  ];

  for (const [domain, rawSection] of evidenceSections) {
    // If a section is completely missing, synthesize an 'unavailable' placeholder 
    // so the domain still renders in the UI (so users can see that sections like 
    // technology or fuelSupply are intentionally evaluated but have no data yet).
    const section = rawSection || ({
      status: 'unavailable',
      data: null,
      confidence: 0,
      source: 'Not available',
      anomalies: [],
    } as unknown as EvidenceSection<any>);

    let entries: [string, any][] = [];
    if (section.data) {
      entries = documentFieldEntries(domain, section.data);
    } else {
      // Force empty sections to show by providing a placeholder variable.
      // We must use `null` as the value, otherwise `classifySlot` treats it as 
      // present data and incorrectly badges the domain as "VERIFIED".
      if (domain === 'technology') entries = [['reactorDesignClass', null]];
      else if (domain === 'fuelSupply') entries = [['naturalGasPriceUsdPerMmbtu', null]];
      else entries = [['status', null]];
    }

    if (entries.length === 0) continue;

    // PRV-1 — project the section's ranked doc refs (preferred) once
    // per section. Every variable in this section shares the section-
    // level lineage; the browser reads the first ref for the inline
    // strip and the rest go behind a disclosure.
    const sectionLinkedRefs = section.linkedDocumentRefs;
    // PRV-2 — project the section's report-derived provenance once
    // per section, same back-compat reasoning.
    const sectionDerivedFromReport = section.provenanceDetail?.derivedFromReport;

    for (const [key, value] of entries) {
      if (EXCLUDED_KEYS.has(key)) continue;
      // Skip complex nested objects (monthlySummary, reactorList, etc.)
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') continue;
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) continue;

      const path = `${domain}.data.${key}`;
      // PRV-2 — claims that explicitly target this variable's path
      // (either via `sedField` or one of the `bindsToSedPaths`
      // entries). Section-level claims (e.g. one that binds to the
      // whole `'climate'` section rather than `'climate.data.avgTemp'`)
      // also fall through here when the registry binds them down to
      // the leaf path — claims that ONLY bind to the section root
      // are skipped by design: a per-variable lineage strip should
      // surface per-variable evidence, not section-wide.
      const matchedClaims = claimsByPath.get(path);

      // DCO-4 — project the section's researchSource onto every variable
      // derived from a research-filled section so the row can render a
      // "Sourced via AI Research" chip with a clickable URL.
      // Constitutional invariant: a 'verified-from-research' status
      // MUST carry a non-empty sourceUrl; we still null-guard
      // defensively for legacy SEDs.
      const researchSource = section.status === 'verified-from-research'
        && section.provenanceDetail?.researchSource?.sourceUrl
        ? {
            sourceUrl: section.provenanceDetail.researchSource.sourceUrl,
            sourceTitle: section.provenanceDetail.researchSource.sourceTitle,
            retrievedAt: section.provenanceDetail.researchSource.retrievedAt,
            confidence: section.provenanceDetail.researchSource.confidence,
            supportingQuote: section.provenanceDetail.researchSource.supportingQuote,
          }
        : undefined;

      const damSource = section.status === 'verified-from-dam'
        && section.provenanceDetail?.damSource
        ? {
            damVersion: section.provenanceDetail.damSource.damVersion,
            damEntryId: section.provenanceDetail.damSource.damEntryId,
            retrievedAt: section.provenanceDetail.damSource.retrievedAt,
            confidence: section.provenanceDetail.damSource.confidence,
          }
        : undefined;

      const history = historyByPath.get(path) ?? [];
      const latestApplied = history.find(a => a.status === 'applied' && !a.revertedFromAmendmentId && !a.id.startsWith('revert-'));
      const authoredBinding = authoredFieldIndex.get(path);
      const acreageDisagreement = domain === 'environmental' && (key === 'wetlandAcres' || key === 'wetlandAcreage')
        && Boolean(section.data?.wetlandSourceAcreageConflicts?.length || section.data?.wetlandAcreageIssues?.length);
      const extractedValue = value;
      const shouldOverlayAuthoredBinding =
        authoredBinding != null &&
        (value == null ||
          section.status === 'verified-from-context' ||
          (section.authoredClaimIds ?? []).includes(authoredBinding.claimId));
      const displayValue = latestApplied != null
        ? (latestApplied.newValue as string | number | boolean | null)
        : (shouldOverlayAuthoredBinding ? (authoredBinding.value as string | number | boolean | null) : value);
      const isUserAmended = latestApplied != null;
      const documentFieldSource = !isUserAmended && authoredBinding == null
        ? currentDocumentFieldSource(section, key) : undefined;
      const leafKey = key.split('.').at(-1)!;
      const fieldRefs = documentFieldSource ? [{
        docId: documentFieldSource.docId, role: 'primary' as const, score: documentFieldSource.confidence,
        rationale: 'Source of this exact adopted field',
        evidenceQuotes: documentFieldSource.excerptPreview ? [documentFieldSource.excerptPreview] : [],
        pageNumber: documentFieldSource.pageNumber, excerptPreview: documentFieldSource.excerptPreview,
        ...(documentFieldSource.sourceOffset ? { sourceOffset: documentFieldSource.sourceOffset } : {}),
      }] : (key.includes('.') ? undefined : sectionLinkedRefs);

      variables.push({
        path,
        label: key.includes('.')
          ? key.split('.').map(part => VARIABLE_LABELS[part] || part.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase())).join(' · ')
          : VARIABLE_LABELS[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()),
        domain,
        domainLabel: DOMAIN_LABELS[domain] || domain,
        value: acreageDisagreement ? null : displayValue,
        extractedValue,
        isUserAmended,
        unit: unitForDocumentField(key) ?? VARIABLE_UNITS[leafKey],
        source: section.source,
        confidence: section.confidence,
        status: acreageDisagreement ? 'unavailable' : section.status,
        // Structured leaves mirror an atomic evidence object; existing field writers
        // must not be offered a new dotted amendment target.
        editable: !key.includes('.') && !acreageDisagreement && section.status !== 'unavailable',
        pendingAmendment: pendingByPath.get(path),
        // LB3-2 — empty array is the explicit "no edits yet" signal.
        amendmentHistory: history,
        // PRV-1 — projection is undefined when the section has no
        // ranked refs (legacy SED before DM4). The browser's strip
        // is gated on `linkedDocumentRefs?.[0]` so the absence is a
        // no-op visually.
        linkedDocumentRefs: fieldRefs,
        documentFieldSource,
        // PRV-2 — only attach claims when there's at least one match.
        // An empty array would still pass the optional-chain truthy
        // checks in the browser; `undefined` keeps the strip silent.
        authoredClaims: matchedClaims && matchedClaims.length > 0
          ? matchedClaims.map(c => ({
              id: c.id,
              sourceSpan: c.sourceSpan,
              claim: c.claim,
              writtenBy: c.lastWrittenBy ?? c.writtenBy,
              lastWrittenAt: c.lastWrittenAt,
              sourceDocId: c.sourceDocId,
            }))
          : undefined,
        // PRV-2 — section-level report provenance applies to every
        // variable that section produces. When the SED resolver gets
        // more granular (per-variable), this will need a per-key
        // lookup; for now section-level is the source of truth.
        derivedFromReport: sectionDerivedFromReport,
        // DCO-4 — section-level research source (undefined unless
        // section.status === 'verified-from-research' with a real URL).
        researchSource,
        // DCO Wave-B — section-level DAM source (undefined unless
        // section.status === 'verified-from-dam' with provenance).
        damSource,
        // PRE-1 — project the section-level Provider Evidence Honesty
        // fields onto every variable the section produces. The
        // renderer reads `attemptedProvider` (paired with a null
        // `value`) to surface the "⚠ Provider — reason" chip instead
        // of either a fabricated success or a silent gap. All five
        // fields are optional — legacy SEDs read them as undefined
        // (renderer no-ops), which matches the pre-PRE behaviour
        // pixel-for-pixel.
        attemptedProvider: section.attemptedProvider,
        attemptResult: section.attemptResult,
        lastAttemptedAt: section.lastAttemptedAt,
        nextRetryAt: section.nextRetryAt,
        emptyReason: section.emptyReason,
        // DC-2 — project EAS-4's `attemptStages` so the PRE chip
        // tooltip can surface the per-stage cascade audit trail
        // (vertex / in-polygon-grid / buffered-bbox-grid / usgs-epqs).
        // Stamped onto siteProfile by `siteEvidence.ts` from the
        // boundary-confirm + KMZ-import + on-demand-refresh paths
        // (DC-3). Optional — legacy SEDs project as undefined.
        attemptStages: section.attemptStages,
      });
    }
  }

  variables.push(...extractPublicSentimentVariables(sed.publicSentiment));

  // GAP 3 — append the NEW 3-axis BTM model rows (cooling axes + power-basis +
  // ELCC / firm-capacity). Read-only mirrors of the user's declared intent, so
  // the browser shows the model the Go/No-Go + reports actually consume.
  variables.push(...extractProjectIntentVariables(sed));

  return variables;
}

/**
 * Apply approved amendments to a SED, producing a new amended copy.
 * Does NOT modify the input SED.
 */
export function applySEDAmendments(
  sed: SiteEvidenceDocument,
  amendments: SEDAmendment[],
): SiteEvidenceDocument {
  // Deep clone the SED
  const amended = JSON.parse(JSON.stringify(sed)) as SiteEvidenceDocument;

  for (const amendment of amendments) {
    if (amendment.status !== 'approved') continue;

    // Navigate the path and set the new value
    const parts = amendment.variablePath.split('.');
    let target: any = amended;
    for (let i = 0; i < parts.length - 1; i++) {
      if (target == null) break;
      target = target[parts[i]];
    }
    if (target != null) {
      const lastKey = parts[parts.length - 1];
      target[lastKey] = amendment.newValue;
    }

    // Record in the SED's amendments array
    amended.amendments.push({
      field: amendment.variablePath,
      originalValue: amendment.oldValue,
      correctedValue: amendment.newValue,
      correctedBy: amendment.createdByEmail,
      reason: amendment.reason,
      source: amendment.source.label,
      timestamp: amendment.createdAt,
    });
  }

  // Bump version
  amended.version = (amended.version ?? 1) + 1;

  return amended;
}

/**
 * UD3 — Find amendments whose `variablePath` no longer maps to an
 * extractable variable in the SED.
 *
 * Schema drift case: when a SED rebuild changes the schema path for
 * a variable (e.g. `environmental.data.species` →
 * `environmental.data.speciesConcerns`), the amendment's `path`
 * field would no longer resolve to a freshly-extracted variable.
 * The amendment becomes orphaned silently — `applySEDAmendments`
 * walks the path, finds the parent but no last-key match, then
 * sets a property that nothing reads downstream.
 *
 * This helper returns the orphan list so:
 *   1. sedBootstrap can log `[sed-bootstrap] orphaned amendments: N`
 *   2. The admin sub-panel can offer a "Re-map to path: …" affordance
 *   3. The UD7 audit log records orphan counts via the preservation
 *      audit event
 *
 * Pure / side-effect-free. Identity match is exact string equality
 * on `variablePath` against the freshly-extracted variables' `path`.
 */
export function findOrphanedAmendments(
  sed: SiteEvidenceDocument,
  amendments: SEDAmendment[],
): SEDAmendment[] {
  if (!amendments || amendments.length === 0) return [];
  const freshVars = extractSEDVariables(sed);
  const freshPaths = new Set(freshVars.map(v => v.path));
  return amendments.filter(a => !freshPaths.has(a.variablePath));
}

/**
 * SA2 — Apply a single amendment to a SED in-place and write the
 * updated SED back to GCS. This is the "write-through" path used when
 * the org policy is "amendments take effect immediately" (the default).
 *
 * Steps:
 *   1. Deep-clone the SED so the caller's reference is not mutated until
 *      the GCS write succeeds.
 *   2. Walk the dotted variablePath ("climate.data.avgTemp") and replace
 *      the leaf value with `amendment.newValue`. Path-resolution failure
 *      throws — the caller (saveSEDAmendment) catches and flips status
 *      back to 'pending' so the SED is never partially-applied.
 *   3. Append a record to `sed.amendments[]` so the in-band audit trail
 *      (read by report regeneration + the LB3 history drawer) advances.
 *   4. Bump `version` and stamp `collectedAt` with the apply time.
 *   5. POST the new SED to /api/storage/save at the object the site's
 *      canonical `sedStoragePath` names — resolved through
 *      `resolveSedInPlaceSavePath`, NOT built from `uid`. HTTP failure
 *      throws — the caller is responsible for the user-facing toast.
 *
 * `canonicalStoragePath` is the owning site's `sedStoragePath`. It is what
 * makes the write land on the object the READ side (`sedLoader.loadSedById`,
 * which tries `site.sedStoragePath` first) actually loads. Omitting it on a
 * company-shared or legacy-flat site writes a full amended SED into the
 * caller's own namespace, which nothing reads — while `site.sedVersion` is
 * still bumped, so every staleness check believes the amendment landed.
 * Callers that can reach the site doc MUST pass it.
 *
 * Returns the updated SED on success. On failure leaves the input SED
 * untouched and throws — caller surfaces the error. The saved object key is
 * `resolveSedInPlaceSavePath(uid, siteId, sed.id, canonicalStoragePath)` —
 * the same pure call the store makes to keep `site.sedStoragePath` in sync.
 */
export function validateAmendmentPathOnSed(
  sed: SiteEvidenceDocument,
  variablePath: string,
  newValue?: unknown,
): void {
  validateAmendmentPathOnSedPure(sed, variablePath, newValue);
}

export async function applySingleAmendmentToSed(
  uid: string,
  siteId: string,
  sed: SiteEvidenceDocument,
  amendment: SEDAmendment,
  canonicalStoragePath?: string | null,
): Promise<SiteEvidenceDocument> {
  if (!uid) throw new Error('applySingleAmendmentToSed: missing uid');
  if (!siteId) throw new Error('applySingleAmendmentToSed: missing siteId');
  if (!sed) throw new Error('applySingleAmendmentToSed: missing sed');
  if (!amendment) throw new Error('applySingleAmendmentToSed: missing amendment');

  const cleanSed = applySingleAmendmentToSedPure(sed, amendment);
  const next = cleanSed;
  const path = resolveSedInPlaceSavePath(uid, siteId, next.id, canonicalStoragePath);
  const { stageAndPublishSedDocument } = await import('./sedLoader');
  const published = await stageAndPublishSedDocument({
    sed: cleanSed,
    storagePath: path,
    siteId,
    ownerUid: uid,
    operationId: amendment.operationId ? `stage-amend-${amendment.operationId}` : undefined,
  });
  if (!published.ok) {
    throw new Error(
      published.error || `Storage write or pointer publication failed for ${path}`,
    );
  }

  return published.sed;
}

/**
 * AMP-2 — Walk a dotted variablePath on a SED and read the leaf value
 * without mutating anything. Returns `undefined` when any segment of
 * the path is missing (schema drift, partial SED, etc.) — callers use
 * this signal to flag amendments whose path no longer resolves.
 *
 * Pure / side-effect-free. Mirrors the path walk in
 * `applySingleAmendmentToSed` but stops at the leaf and reads instead
 * of writing.
 */
export function readValueAtSEDPath(
  sed: SiteEvidenceDocument,
  variablePath: string,
): unknown {
  const parts = variablePath.split('.');
  if (parts.length === 0) return undefined;
  let cursor: unknown = sed;
  for (const part of parts) {
    if (cursor == null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  return cursor;
}

/**
 * AMP-2 — Reapply outcome for a single amendment carried across a SED
 * rebuild. Three terminal states:
 *
 *  - `reapplied` — provider value in the new SED matches the amendment's
 *    `oldValue` (the value the user originally overrode) or matches the
 *    user's `newValue` (the user's amendment already landed in the new
 *    SED, no-op). Reapply runs silently.
 *  - `conflict`  — provider value in the new SED is different from BOTH
 *    `oldValue` and `newValue`. The user's intent to override a specific
 *    provider value can't be auto-applied because the provider has
 *    shifted; surface as an EvidenceConflict for human resolution.
 *  - `orphaned`  — the variablePath doesn't resolve in the new SED at
 *    all (schema drift). Same audit signal as
 *    `findOrphanedAmendments` — the amendment stays as historical
 *    audit; UI surfaces a follow-up.
 *  - `skipped`   — the amendment's status isn't `applied` (e.g. it's
 *    already `reverted` or `superseded`); reapply ignores it.
 *  - `error`     — the apply step threw (storage failure, etc.);
 *    surfaced so callers can decide whether to retry the whole batch.
 */
// F1 (2026-07-02) — the reapply planner moved to a PURE module
// (lib/sed/amendmentReapplyPlanner) so the SERVER bootstrap job can fold prior
// amendments into a fresh SED without pulling this file's client-only deps
// (authedFetch → firebase web auth) into the job bundle. Re-exported here so
// every existing client call site keeps its import path.
export type { AmendmentReapplyOutcome } from '../lib/sed/amendmentReapplyPlanner';
export { planAmendmentReapply } from '../lib/sed/amendmentReapplyPlanner';


/**
 * Compute a diff summary between two SED versions.
 */
export function diffSEDVersions(
  oldSed: SiteEvidenceDocument,
  newSed: SiteEvidenceDocument,
): { path: string; label: string; oldValue: any; newValue: any }[] {
  const oldVars = extractSEDVariables(oldSed);
  const newVars = extractSEDVariables(newSed);
  const newByPath = new Map(newVars.map(v => [v.path, v]));
  const diffs: { path: string; label: string; oldValue: any; newValue: any }[] = [];

  for (const oldVar of oldVars) {
    const newVar = newByPath.get(oldVar.path);
    if (newVar && JSON.stringify(oldVar.value) !== JSON.stringify(newVar.value)) {
      diffs.push({
        path: oldVar.path,
        label: oldVar.label,
        oldValue: oldVar.value,
        newValue: newVar.value,
      });
    }
  }

  return diffs;
}


// ============================================================================
// Complete Immutable Save Request Envelope for Unresolved Submissions
// ============================================================================

export interface PendingSedSaveRequestBody {
  action: 'save';
  operationId: string;
  ownerUid: string;
  sedId: string;
  expectedSedVersion?: number;
  expectedHistoryVersion?: number;
  expectedSedStorageGeneration?: string;
  requireReviewWorkflow?: boolean;
  amendment: SEDAmendment;
}

export interface PendingSedSaveEnvelope {
  actorUid: string;
  ownerUid: string;
  siteId: string;
  sedId: string;
  variablePath: string;
  draftSignature: string;
  operationId: string;
  amendmentId: string;
  expectedSedVersion?: number;
  expectedHistoryVersion?: number;
  expectedSedStorageGeneration?: string;
  requireReviewWorkflow?: boolean;
  amendment: SEDAmendment;
  requestBody: PendingSedSaveRequestBody;
  createdAt: string;
}

export interface PendingSedSaveScope {
  actorUid: string;
  ownerUid: string;
  siteId: string;
  sedId: string;
  variablePath: string;
}

const PENDING_SED_SAVE_ENVELOPE_PREFIX = 'da-pending-sed-save-envelope:v1:';
const memoryPendingSaveEnvelopes = new Map<string, PendingSedSaveEnvelope[]>();

export function computeSedAmendmentDraftSignature(input: {
  variablePath: string;
  newValue: unknown;
  reason: string;
  sourceType: string;
  sourceLabel: string;
  sourceUrl?: string;
  issueType?: string;
}): string {
  return JSON.stringify([
    input.variablePath,
    input.newValue ?? null,
    input.reason ?? '',
    input.sourceType ?? '',
    input.sourceLabel ?? '',
    input.sourceUrl ?? '',
    input.issueType ?? null,
  ]);
}

export function pendingSedSaveEnvelopeScopeKey(scope: PendingSedSaveScope): string {
  return `${PENDING_SED_SAVE_ENVELOPE_PREFIX}${scope.actorUid}::${scope.ownerUid}::${scope.siteId}::${scope.sedId}::${scope.variablePath}`;
}

function isValidPendingSedSaveEnvelope(
  candidate: unknown,
  scope: PendingSedSaveScope,
): candidate is PendingSedSaveEnvelope {
  if (!candidate || typeof candidate !== 'object') return false;
  const env = candidate as Partial<PendingSedSaveEnvelope>;
  return (
    env.actorUid === scope.actorUid &&
    env.ownerUid === scope.ownerUid &&
    env.siteId === scope.siteId &&
    env.sedId === scope.sedId &&
    env.variablePath === scope.variablePath &&
    typeof env.draftSignature === 'string' &&
    typeof env.operationId === 'string' &&
    env.operationId.length > 0 &&
    typeof env.amendmentId === 'string' &&
    env.amendmentId.length > 0 &&
    Boolean(env.amendment && typeof env.amendment === 'object') &&
    Boolean(
      env.requestBody &&
        typeof env.requestBody === 'object' &&
        env.requestBody.action === 'save' &&
        env.requestBody.operationId === env.operationId &&
        env.requestBody.ownerUid === scope.ownerUid &&
        env.requestBody.sedId === scope.sedId &&
        env.requestBody.amendment &&
        env.requestBody.amendment.id === env.amendmentId,
    )
  );
}

function normalizeStoredEnvelopeList(
  parsed: unknown,
  scope: PendingSedSaveScope,
): PendingSedSaveEnvelope[] {
  if (Array.isArray(parsed)) {
    return parsed.filter((item): item is PendingSedSaveEnvelope =>
      isValidPendingSedSaveEnvelope(item, scope),
    );
  }
  if (isValidPendingSedSaveEnvelope(parsed, scope)) {
    return [parsed];
  }
  return [];
}

function persistEnvelopeList(key: string, list: PendingSedSaveEnvelope[]): void {
  if (list.length === 0) {
    memoryPendingSaveEnvelopes.delete(key);
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.removeItem(key);
      } catch {
        /* ignore storage errors */
      }
    }
    return;
  }
  memoryPendingSaveEnvelopes.set(key, list);
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(key, JSON.stringify(list.length === 1 ? list[0] : list));
    } catch {
      /* ignore storage quota errors */
    }
  }
}

export function readPendingSedSaveEnvelopes(
  scope: PendingSedSaveScope,
): PendingSedSaveEnvelope[] {
  if (!scope.actorUid || !scope.ownerUid || !scope.siteId || !scope.sedId || !scope.variablePath) {
    return [];
  }
  const key = pendingSedSaveEnvelopeScopeKey(scope);
  const mem = memoryPendingSaveEnvelopes.get(key);
  const validMem = mem ? normalizeStoredEnvelopeList(mem, scope) : [];
  if (typeof localStorage !== 'undefined') {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        const validStored = normalizeStoredEnvelopeList(parsed, scope);
        if (validStored.length > 0) {
          const mergedById = new Map<string, PendingSedSaveEnvelope>();
          for (const item of [...validStored, ...validMem]) {
            mergedById.set(item.operationId, item);
          }
          const merged = [...mergedById.values()];
          memoryPendingSaveEnvelopes.set(key, merged);
          return merged;
        }
      }
    } catch {
      /* ignore storage errors */
    }
  }
  return validMem;
}

export function readPendingSedSaveEnvelope(
  scope: PendingSedSaveScope,
  match?: {
    operationId?: string;
    draftSignature?: string;
    amendmentId?: string;
  },
): PendingSedSaveEnvelope | null {
  const list = readPendingSedSaveEnvelopes(scope);
  if (list.length === 0) return null;
  if (match?.operationId) {
    return list.find((e) => e.operationId === match.operationId) ?? null;
  }
  if (match?.draftSignature) {
    return list.find((e) => e.draftSignature === match.draftSignature) ?? null;
  }
  if (match?.amendmentId) {
    return list.find((e) => e.amendmentId === match.amendmentId) ?? null;
  }
  return list[0] ?? null;
}

export function writePendingSedSaveEnvelope(envelope: PendingSedSaveEnvelope): void {
  const key = pendingSedSaveEnvelopeScopeKey(envelope);
  const existing = readPendingSedSaveEnvelopes(envelope);
  const idx = existing.findIndex(
    (e) =>
      e.operationId === envelope.operationId ||
      e.draftSignature === envelope.draftSignature ||
      e.amendmentId === envelope.amendmentId,
  );
  const next = [...existing];
  if (idx >= 0) {
    next[idx] = envelope;
  } else {
    next.push(envelope);
  }
  persistEnvelopeList(key, next);
}

export function clearPendingSedSaveEnvelope(
  scope: PendingSedSaveScope,
  expectedOperationId?: string,
): void {
  if (!scope.actorUid || !scope.ownerUid || !scope.siteId || !scope.sedId || !scope.variablePath) {
    return;
  }
  const key = pendingSedSaveEnvelopeScopeKey(scope);
  if (expectedOperationId) {
    const existing = readPendingSedSaveEnvelopes(scope);
    const next = existing.filter((e) => e.operationId !== expectedOperationId);
    if (next.length === existing.length) {
      return;
    }
    persistEnvelopeList(key, next);
    return;
  }
  persistEnvelopeList(key, []);
}

export function clearPendingSedSaveEnvelopesForTests(): void {
  memoryPendingSaveEnvelopes.clear();
  if (typeof localStorage !== 'undefined') {
    try {
      for (let i = localStorage.length - 1; i >= 0; i -= 1) {
        const k = localStorage.key(i);
        if (k?.startsWith(PENDING_SED_SAVE_ENVELOPE_PREFIX)) {
          localStorage.removeItem(k);
        }
      }
    } catch {
      /* ignore */
    }
  }
}
